import React from 'react';
import { Anuncio } from '../../types';
import AnuncioCard from './AnuncioCard';
import { SkeletonCard, EmptyState } from '../common/Loading';
import { LoadMore } from '../common/Pagination';

interface AnuncioGridProps {
  anuncios: Anuncio[];
  isLoading?: boolean;
  showFavorite?: boolean;
  columns?: 2 | 3 | 4;
  emptyMessage?: string;
  emptyIcon?: string;
  emptyAction?: React.ReactNode;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

const AnuncioGrid: React.FC<AnuncioGridProps> = ({
  anuncios,
  isLoading = false,
  showFavorite = true,
  columns = 4,
  emptyMessage = 'Nenhum anúncio encontrado',
  emptyIcon = '🔍',
  emptyAction,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
}) => {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };

  if (isLoading) {
    return (
      <div className={`grid ${gridCols[columns]} gap-6`}>
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (anuncios.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyMessage}
        description="Tente ajustar os filtros ou faça uma nova busca."
        action={emptyAction}
      />
    );
  }

  return (
    <div>
      <div className={`grid ${gridCols[columns]} gap-6`}>
        {anuncios.map((anuncio) => (
          <AnuncioCard
            key={anuncio.id}
            anuncio={anuncio}
            showFavorite={showFavorite}
          />
        ))}
      </div>
      
      {onLoadMore && (
        <LoadMore
          onClick={onLoadMore}
          isLoading={isLoadingMore}
          hasMore={hasMore}
        />
      )}
    </div>
  );
};

// Compact card for dashboard/listings
export const AnuncioCardCompact: React.FC<{
  anuncio: Anuncio;
  onEdit?: () => void;
  onPause?: () => void;
  onActivate?: () => void;
  onDelete?: () => void;
  onMarkSold?: () => void;
}> = ({ anuncio, onEdit, onPause, onActivate, onDelete, onMarkSold }) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const statusConfig = {
    ativo: { color: 'bg-green-100 text-green-800', label: 'Ativo' },
    pausado: { color: 'bg-amber-100 text-amber-800', label: 'Pausado' },
    vendido: { color: 'bg-blue-100 text-blue-800', label: 'Vendido' },
    expirado: { color: 'bg-gray-100 text-gray-800', label: 'Expirado' },
    pendente: { color: 'bg-orange-100 text-orange-800', label: 'Pendente' },
  };

  const status = statusConfig[anuncio.status];
  const mainImage = anuncio.imagens?.[0]?.url || '/placeholder-animal.jpg';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex gap-4">
        {/* Image */}
        <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
          <img
            src={mainImage}
            alt={anuncio.titulo}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">
                {anuncio.titulo}
              </h3>
              <p className="text-sm text-gray-500">
                {anuncio.localizacao?.cidade}, {anuncio.localizacao?.estado}
              </p>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${status.color}`}>
              {status.label}
            </span>
          </div>

          <div className="flex items-center justify-between mt-3">
            <p className="text-lg font-bold text-green-700">
              {formatPrice(anuncio.preco)}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {anuncio.visualizacoes}
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                {anuncio.favoritosCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
        {onEdit && (
          <button
            onClick={onEdit}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Editar
          </button>
        )}
        {anuncio.status === 'ativo' && onPause && (
          <button
            onClick={onPause}
            className="flex-1 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
          >
            Pausar
          </button>
        )}
        {anuncio.status === 'pausado' && onActivate && (
          <button
            onClick={onActivate}
            className="flex-1 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 rounded-lg transition-colors"
          >
            Ativar
          </button>
        )}
        {anuncio.status !== 'vendido' && onMarkSold && (
          <button
            onClick={onMarkSold}
            className="flex-1 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Vendido
          </button>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default AnuncioGrid;
